import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { authAPI } from '../../services/api';

// Project options organized by type and category
const PROJECT_OPTIONS = {
    mini: {
        DEVELOPERS: [
            'Smart ATS Resume Analyzer & Optimizer',
            'AI-Powered Portfolio Generator',
            'Smart Complaint & Ticket Management System',
            'Role-Based E-Learning Platform',
            'WhatsApp Business Automation Tool',
        ],
        DATA_ANALYST: [
            'Sales & Customer Insights Dashboard',
            'Job Market Trend Analyzer',
        ],
        AI_ML: [
            'AI Resume Score Predictor',
            'Face Emotion Detection System',
            'Fake News Detection Using ML',
        ],
    },
    major: {
        DEVELOPERS: [
            'AI Job Application Automation',
            'Multi Vendor Ecommerce',
            'Smart College Management System',
            'Stock Market Simulation',
            'SaaS No Code Website Builder',
        ],
        DATA_ANALYST: [
            'EV Vehicle Dashboard',
            'Blinkit Sales Dashboard',
        ],
        AI_ML: [
            'AI Interview Evaluation System',
            'Intelligent Video Surveillance',
            'AI Content Moderation System',
        ],
    },
};

function RegisterStudent() {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        category: 'DEVELOPERS',
        miniProjectId: '',
        majorProjectId: '',
    });
    const [loading, setLoading] = useState(false);

    // Get project options based on category
    const getMiniProjectOptions = useCallback(() => {
        return PROJECT_OPTIONS.mini[formData.category] || [];
    }, [formData.category]);

    const getMajorProjectOptions = useCallback(() => {
        return PROJECT_OPTIONS.major[formData.category] || [];
    }, [formData.category]);

    // Reset project selections when category changes
    useEffect(() => {
        setFormData((prev) => ({ ...prev, miniProjectId: '', majorProjectId: '' }));
    }, [formData.category]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.miniProjectId || !formData.majorProjectId) {
            toast.error('Please select both mini and major projects');
            return;
        }

        setLoading(true);

        try {
            await authAPI.registerStudent(formData);
            toast.success('Student registered successfully!');
            setFormData({
                name: '',
                email: '',
                password: '',
                category: 'DEVELOPERS',
                miniProjectId: '',
                majorProjectId: '',
            });
        } catch (error) {
            toast.error(error.response?.data?.message || 'Registration failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="component-container">
            <h2>Register New Student</h2>

            <form onSubmit={handleSubmit} className="student-form">
                <div className="form-grid">
                    <div className="form-group">
                        <label htmlFor="name">Student Name *</label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            placeholder="Enter student name"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="email">Email *</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            placeholder="student@example.com"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">Password *</label>
                        <input
                            type="password"
                            id="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            required
                            placeholder="Enter password"
                            minLength="6"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="category">Category *</label>
                        <select
                            id="category"
                            name="category"
                            value={formData.category}
                            onChange={handleChange}
                            required
                        >
                            <option value="DEVELOPERS">Developers</option>
                            <option value="DATA_ANALYST">Data Analyst</option>
                            <option value="AI_ML">AI/ML</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label htmlFor="miniProjectId">Mini Project *</label>
                        <select
                            id="miniProjectId"
                            name="miniProjectId"
                            value={formData.miniProjectId}
                            onChange={handleChange}
                            required
                        >
                            <option value="">Select Mini Project</option>
                            {getMiniProjectOptions().map((projectName) => (
                                <option key={projectName} value={projectName}>
                                    {projectName}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label htmlFor="majorProjectId">Major Project *</label>
                        <select
                            id="majorProjectId"
                            name="majorProjectId"
                            value={formData.majorProjectId}
                            onChange={handleChange}
                            required
                        >
                            <option value="">Select Major Project</option>
                            {getMajorProjectOptions().map((projectName) => (
                                <option key={projectName} value={projectName}>
                                    {projectName}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Registering...' : 'Register Student'}
                </button>
            </form>
        </div>
    );
}

export default RegisterStudent;
